import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, TagIcon } from '@heroicons/react/24/outline';
import type { Etiqueta } from '../../types';

// Paleta de cores sugeridas para novas etiquetas
const CORES_SUGERIDAS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
  '#10B981', '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
  '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#64748B', '#0F172A',
];

const gerarId = () => 'etq_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);

// Retorna cor de texto legível (preto/branco) para um fundo hex
export const corTextoParaFundo = (hex: string): string => {
  try {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminancia > 0.6 ? '#1f2937' : '#ffffff';
  } catch {
    return '#ffffff';
  }
};

// ====================== EXIBIÇÃO DE ETIQUETAS (badges) ======================
export function EtiquetaBadges({
  etiquetasIds,
  etiquetas,
  tamanho = 'sm',
  max,
}: {
  etiquetasIds?: string[];
  etiquetas?: Etiqueta[];
  tamanho?: 'xs' | 'sm';
  max?: number;
}) {
  if (!etiquetasIds || etiquetasIds.length === 0 || !etiquetas || etiquetas.length === 0) return null;
  const vinculadas = etiquetasIds
    .map(id => etiquetas.find(e => e.id === id))
    .filter((e): e is Etiqueta => !!e);
  if (vinculadas.length === 0) return null;

  const mostrar = max ? vinculadas.slice(0, max) : vinculadas;
  const restantes = max ? vinculadas.length - mostrar.length : 0;

  const pad = tamanho === 'xs' ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-0.5 text-[10px]';

  return (
    <div className="flex flex-wrap items-center gap-1">
      {mostrar.map(etq => (
        <span
          key={etq.id}
          className={`inline-flex items-center rounded-full font-semibold leading-none ${pad}`}
          style={{ backgroundColor: etq.cor, color: corTextoParaFundo(etq.cor) }}
          title={etq.nome}
        >
          {etq.nome}
        </span>
      ))}
      {restantes > 0 && (
        <span className={`inline-flex items-center rounded-full font-semibold leading-none bg-gray-200 text-gray-600 ${pad}`}>
          +{restantes}
        </span>
      )}
    </div>
  );
}

// ====================== SELETOR DE ETIQUETAS (popover) ======================
// Permite vincular/desvincular etiquetas de um card. Opcionalmente permite
// gerenciar a biblioteca (criar nova etiqueta) se onGerenciar for fornecido.
export function EtiquetasPicker({
  etiquetas,
  selecionadas,
  onToggle,
  onGerenciar,
  disabled,
}: {
  etiquetas: Etiqueta[];
  selecionadas: string[];
  onToggle: (etiquetaId: string) => void;
  onGerenciar?: () => void;
  disabled?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [aberto]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setAberto(v => !v)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium transition-colors ${
          disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
        title="Vincular etiquetas"
      >
        <TagIcon className="h-3.5 w-3.5" />
        Etiquetas
      </button>

      {aberto && (
        <div className="absolute left-0 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 max-h-72 overflow-y-auto">
          {etiquetas.length === 0 && (
            <p className="px-3 py-3 text-xs text-gray-400 text-center">Nenhuma etiqueta criada ainda.</p>
          )}
          {etiquetas.map(etq => {
            const ativa = selecionadas.includes(etq.id);
            return (
              <button
                key={etq.id}
                type="button"
                onClick={() => onToggle(etq.id)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 transition-colors text-left"
              >
                <span
                  className="flex-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none truncate"
                  style={{ backgroundColor: etq.cor, color: corTextoParaFundo(etq.cor) }}
                >
                  {etq.nome}
                </span>
                {ativa && <CheckIcon className="h-4 w-4 text-indigo-600 flex-shrink-0" />}
              </button>
            );
          })}
          {onGerenciar && (
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                type="button"
                onClick={() => { setAberto(false); onGerenciar(); }}
                className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                <PlusIcon className="h-3.5 w-3.5" /> Gerenciar etiquetas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ====================== MODAL CONFIGURADOR DE ETIQUETAS ======================
export function GerenciarEtiquetasModal({
  etiquetas,
  onSalvar,
  onClose,
}: {
  etiquetas: Etiqueta[];
  onSalvar: (etiquetas: Etiqueta[]) => void;
  onClose: () => void;
}) {
  const [lista, setLista] = useState<Etiqueta[]>(etiquetas.map(e => ({ ...e })));
  const [novoNome, setNovoNome] = useState('');
  const [novaCor, setNovaCor] = useState(CORES_SUGERIDAS[10]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');
  const [editCor, setEditCor] = useState('');

  const adicionar = () => {
    const nome = novoNome.trim();
    if (!nome) return;
    setLista(prev => [...prev, { id: gerarId(), nome, cor: novaCor }]);
    setNovoNome('');
    setNovaCor(CORES_SUGERIDAS[Math.floor(Math.random() * CORES_SUGERIDAS.length)]);
  };

  const iniciarEdicao = (etq: Etiqueta) => {
    setEditandoId(etq.id);
    setEditNome(etq.nome);
    setEditCor(etq.cor);
  };

  const salvarEdicao = () => {
    const nome = editNome.trim();
    if (!nome) return;
    setLista(prev => prev.map(e => e.id === editandoId ? { ...e, nome, cor: editCor } : e));
    setEditandoId(null);
  };

  const remover = (id: string) => {
    setLista(prev => prev.filter(e => e.id !== id));
  };

  const handleSalvar = () => {
    onSalvar(lista);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="flex items-center gap-2">
            <TagIcon className="h-5 w-5" />
            <div>
              <h2 className="text-base font-semibold">Etiquetas do fluxo</h2>
              <p className="text-[11px] text-indigo-100">Crie etiquetas para classificar, filtrar e gerar relatórios dos cards.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Criar nova etiqueta */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <label className="block text-[10px] font-medium text-gray-600 mb-1.5 uppercase tracking-wider">Nova etiqueta</label>
            <div className="flex items-center gap-2">
              <input
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') adicionar(); }}
                placeholder="Ex.: Urgente"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <input
                type="color"
                value={novaCor}
                onChange={(e) => setNovaCor(e.target.value)}
                className="h-9 w-10 border border-gray-300 rounded cursor-pointer flex-shrink-0"
                title="Cor"
              />
              <button
                type="button"
                onClick={adicionar}
                disabled={!novoNome.trim()}
                className="flex items-center gap-1 px-3 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <PlusIcon className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {CORES_SUGERIDAS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNovaCor(c)}
                  className={`w-5 h-5 rounded-full transition-transform ${novaCor === c ? 'ring-2 ring-offset-1 ring-gray-700 scale-110' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>

          {/* Lista de etiquetas */}
          <div className="space-y-2">
            {lista.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">Nenhuma etiqueta criada. Adicione a primeira acima.</p>
            )}
            {lista.map(etq => (
              <div key={etq.id} className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg">
                {editandoId === etq.id ? (
                  <>
                    <input
                      type="color"
                      value={editCor}
                      onChange={(e) => setEditCor(e.target.value)}
                      className="h-8 w-9 border border-gray-300 rounded cursor-pointer flex-shrink-0"
                    />
                    <input
                      value={editNome}
                      onChange={(e) => setEditNome(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') salvarEdicao(); }}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      autoFocus
                    />
                    <button onClick={salvarEdicao} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Salvar">
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => setEditandoId(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors" title="Cancelar">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold leading-none"
                      style={{ backgroundColor: etq.cor, color: corTextoParaFundo(etq.cor) }}
                    >
                      {etq.nome}
                    </span>
                    <div className="flex-1" />
                    <button onClick={() => iniciarEdicao(etq)} className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Editar">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => remover(etq.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50/80 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSalvar} className="px-5 py-2 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors">
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
